import axios from 'axios';
import {
  BUILDINGS_SELECTOR_FETCH_LIMIT,
  BuildingsListResponse,
  isPaginatedBuildingsResponse,
  unwrapBuildingsList,
} from './buildingsList.util';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create axios instance with interceptor for authentication
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface Building {
  id: string;
  name: string;
  operator: {
    id: string;
    name: string;
  };
}

export interface ApartmentImportErrorSummary {
  message: string;
  count: number;
  rows: number[];
  codes: string[];
}

export interface ApartmentImportBatch {
  id: string;
  buildingId: string;
  fileName: string;
  totalRows: number;
  importedCount: number;
  updatedCount: number;
  renamedCount: number;
  skippedCount: number;
  missingCount: number;
  quantityUpdateCount: number;
  errorCount: number;
  status: 'SUCCESS' | 'PARTIAL' | 'FAILED';
  errors?: string[] | null;
  warnings?: { missingCodes?: string[] } | null;
  metadata?: {
    skippedCodes?: string[];
    errorSummary?: ApartmentImportErrorSummary[];
    existingCount?: number;
    fileApartmentCount?: number;
  } | null;
  affectedItems?: unknown;
  revertedAt?: string | null;
  revertedById?: string | null;
  revertRestoredCount?: number | null;
  reverter?: { id: string; name: string; email?: string | null } | null;
  createdAt: string;
  creator?: { id: string; name: string; email?: string | null } | null;
}

export interface ApartmentBulkDeleteSkipped {
  id: string;
  code: string;
  reasons: string[];
}

export interface ApartmentBulkDeleteResult {
  deletedCount: number;
  deletedCodes: string[];
  skipped: ApartmentBulkDeleteSkipped[];
}

export interface ResidentBulkDeleteResult {
  deletedCount: number;
  deletedMemberIds: string[];
  deletedNames: Array<{ id: string; name: string; apartmentCode: string }>;
  skipped: Array<{ id: string; name: string; apartmentCode: string; reasons: string[] }>;
}

export type ResidentActivityHistoryKind = 'CREATE' | 'UPDATE' | 'DELETE' | 'BULK_DELETE' | 'IMPORT';

export interface ResidentActivityHistoryItem {
  kind: ResidentActivityHistoryKind;
  id: string;
  createdAt: string;
  creator?: { id: string; name: string; email?: string | null } | null;
  memberName?: string | null;
  apartmentCode?: string | null;
  action?: 'CREATE' | 'UPDATE' | 'DELETE' | 'BULK_DELETE' | 'IMPORT';
  summary?: string;
  fileName?: string | null;
  fileUrl?: string | null;
  metadata?: Record<string, unknown> | null;
}

export type ApartmentActivityHistoryKind = 'IMPORT' | 'UPDATE' | 'DELETE' | 'BULK_DELETE';

export interface ApartmentActivityHistoryItem {
  kind: ApartmentActivityHistoryKind;
  id: string;
  createdAt: string;
  creator?: { id: string; name: string; email?: string | null } | null;
  fileName?: string;
  fileUrl?: string | null;
  totalRows?: number;
  importedCount?: number;
  updatedCount?: number;
  renamedCount?: number;
  skippedCount?: number;
  missingCount?: number;
  quantityUpdateCount?: number;
  errorCount?: number;
  status?: ApartmentImportBatch['status'];
  errors?: string[] | null;
  warnings?: { missingCodes?: string[] } | null;
  metadata?: Record<string, unknown> | null;
  revertedAt?: string | null;
  revertRestoredCount?: number | null;
  hasAffectedQuantities?: boolean;
  apartmentCode?: string | null;
  action?: 'UPDATE' | 'DELETE' | 'BULK_DELETE';
  summary?: string;
}

export interface ApartmentGroup {
  id: string;
  name: string;
  description?: string;
  buildingId: string;
  building: Building;
  apartmentCount?: number;
  _count?: {
    apartments: number;
  };
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
}

export interface ApartmentUsageType {
  id: string;
  name: string;
  code: string;
  description?: string;
  managementFeeCode?: string | null;
  isDefault: boolean;
  buildingId: string;
  building: Building;
  apartmentCount?: number;
  residentCount?: number;
  _count?: {
    apartments: number;
  };
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
}

export interface UsageTypeImportResult {
  success: boolean;
  message: string;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  skippedCodes: string[];
  errors: string[];
}

export type ApartmentOccupancyStatus = 'OWNER_USING' | 'RENTED' | 'FOR_RENT' | 'FOR_SALE';

export interface Apartment {
  id: string;
  code: string;
  customerCode?: string;
  name?: string;
  occupancyStatus?: ApartmentOccupancyStatus;
  handoverDate?: string | null;
  peCode?: string;
  populationCount?: number;
  ownerName: string;
  ownerPhone?: string;
  ownerEmail?: string;
  invoiceEmail?: string;
  address?: string;
  area?: number;
  taxCode?: string;
  notes?: string;
  hasVatInvoice?: boolean;
  buildingId: string;
  building: Building;
  groupId?: string;
  group?: ApartmentGroup;
  usageTypeId?: string;
  usageType?: ApartmentUsageType;
  registeredQuotaCount?: number;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
}

export interface CreateApartmentData {
  code: string;
  customerCode?: string;
  name?: string;
  occupancyStatus?: ApartmentOccupancyStatus;
  handoverDate?: string | null;
  peCode?: string;
  populationCount?: number;
  ownerName: string;
  ownerPhone?: string;
  ownerEmail?: string;
  invoiceEmail?: string;
  address?: string;
  area?: number;
  taxCode?: string;
  notes?: string;
  hasVatInvoice?: boolean;
  buildingId: string;
  groupId?: string;
  usageTypeId?: string;
  registeredQuotaCount?: number;
}

export interface UpdateApartmentData extends Partial<CreateApartmentData> {
  id: string;
}

export interface CreateApartmentGroupData {
  name: string;
  description?: string;
  buildingId: string;
}

export interface UpdateApartmentGroupData extends Partial<CreateApartmentGroupData> {
  id: string;
}

export interface CreateApartmentUsageTypeData {
  name: string;
  code: string;
  description?: string;
  managementFeeCode?: string | null;
  isDefault?: boolean;
  buildingId: string;
}

export interface UpdateApartmentUsageTypeData extends Partial<CreateApartmentUsageTypeData> {
  id: string;
}

export type ResidentRole = 'CHU_HO' | 'NHAN_KHAU' | 'OWNER' | 'TENANT' | 'FAMILY' | 'CORPORATE_REPRESENTATIVE' | 'CORPORATE_EMPLOYEE';
export type ResidentGender = 'MALE' | 'FEMALE' | 'OTHER';

export interface ApartmentMember {
  id: string;
  fullName: string;
  dateOfBirth?: string;
  relationshipCode?: string;
  apartmentId: string;
  apartment?: Apartment;
  userId?: string;
  isPrimary: boolean;
  role: ResidentRole;
  gender?: ResidentGender;
  nationality?: string;
  joinedAt: string;
  moveInDate?: string;
  moveOutDate?: string;
  email?: string;
  phone?: string;
  citizenId?: string;
  idIssueDate?: string;
  idExpiryDate?: string;
  notes?: string;
  invoiceEmail?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
    userType: 'RESIDENT' | 'CORPORATE';
    lastLoginAt?: string | null;
  } | null;
}

export interface PaginatedResidents {
  data: ApartmentMember[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    ownerCount?: number;
    appLinkedCount?: number;
  };
}

export interface ResidentListParams {
  buildingId?: string;
  apartmentId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface FeeTier {
  id: string;
  tierLevel: number;
  usageFrom?: number;
  usageTo?: number;
  price: number;
  unit?: string;
}

export interface FeeCategory {
  id: string;
  name: string;
  feeCode?: string;
  pricingType: 'SINGLE_TIER' | 'TIERED' | 'QUOTA_BASED';
  basePrice?: number;
  vatPercentage: number;
  isMonthlyFixed: boolean;
  autoCloseMonthly: boolean;
  isActive: boolean;
  notes?: string;
  quantitySource?: 'MANUAL' | 'APARTMENT_AREA' | 'FIXED_ONE';
  quantityUnit?: string | null;
  feeTiers: FeeTier[];
  
  // Quota-based pricing fields
  quotaLimit?: number;
  quotaPrice?: number;
  overQuotaPrice1?: number;
  overQuotaPrice2?: number;
  wastagePercentage?: number;
  environmentalFee?: number;
  quotaCalculationType?: string;
  
  // Base prices for quota-based pricing (without VAT and environmental fees)
  baseQuotaPrice?: number;
  baseOverQuotaPrice1?: number;
  baseOverQuotaPrice2?: number;

  extraFee1Percent?: number;
  extraFee1Amount?: number | null;
  extraFee1Fixed?: number;
  extraFee1Name?: string | null;
  extraFee2Percent?: number;
  extraFee2Amount?: number | null;
  extraFee2Fixed?: number;
  extraFee2Name?: string | null;
}

export interface ApartmentFeeCategory {
  id: string | null;
  apartmentId: string;
  feeCategoryId: string;
  quantity: number;
  effectiveQuantity?: number;
  quotaLimit?: number;
  notes: string;
  feeCategory: FeeCategory;
}

export interface CreateApartmentMemberData {
  fullName: string;
  dateOfBirth?: string | null;
  relationshipCode?: string | null;
  apartmentId: string;
  userId?: string;
  isPrimary?: boolean;
  role?: ResidentRole;
  gender?: ResidentGender | null;
  nationality?: string | null;
  moveInDate?: string | null;
  moveOutDate?: string | null;
  email?: string | null;
  phone?: string | null;
  citizenId?: string | null;
  idIssueDate?: string | null;
  idExpiryDate?: string | null;
  notes?: string | null;
  invoiceEmail?: string | null;
}

export type UpdateApartmentMemberData = Partial<CreateApartmentMemberData>;

export interface ApartmentListParams {
  buildingId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedApartments {
  data: Apartment[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// API Functions
export const apartmentsApi = {
  // Apartments
  getApartments: async (params?: ApartmentListParams): Promise<Apartment[]> => {
    const allApartments: Apartment[] = [];
    let page = params?.page ?? 1;
    const limit = params?.limit ?? 500;

    while (true) {
      const response = await api.get('/apartments', {
        params: {
          page,
          limit,
          buildingId: params?.buildingId,
          search: params?.search,
          sortBy: 'code',
          sortOrder: 'asc',
        },
      });

      const batch: Apartment[] = response.data.data || response.data || [];
      allApartments.push(...batch);

      const totalPages = response.data.meta?.totalPages ?? 1;
      if (page >= totalPages || batch.length === 0) {
        break;
      }
      page += 1;
    }

    return allApartments;
  },

  getApartmentsPage: async (params?: ApartmentListParams): Promise<PaginatedApartments> => {
    const response = await api.get('/apartments', {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 25,
        buildingId: params?.buildingId,
        search: params?.search,
        sortBy: 'code',
        sortOrder: 'asc',
      },
    });
    return response.data;
  },

  getApartmentById: async (id: string): Promise<Apartment> => {
    const response = await api.get(`/apartments/${id}`);
    return response.data;
  },

  createApartment: async (data: CreateApartmentData): Promise<Apartment> => {
    const response = await api.post('/apartments', data);
    return response.data;
  },

  updateApartment: async (data: UpdateApartmentData): Promise<Apartment> => {
    const { id, ...updateData } = data;
    const response = await api.patch(`/apartments/${id}`, updateData);
    return response.data;
  },

  deleteApartment: async (id: string): Promise<void> => {
    await api.delete(`/apartments/${id}`);
  },

  bulkDeleteApartments: async (
    buildingId: string,
    apartmentIds: string[],
  ): Promise<ApartmentBulkDeleteResult> => {
    const response = await api.post('/apartments/bulk-delete', {
      buildingId,
      apartmentIds,
    });
    return response.data;
  },

  toggleApartmentStatus: async (id: string): Promise<Apartment> => {
    const response = await api.patch(`/apartments/${id}/toggle-status`);
    return response.data;
  },

  // Apartment Groups
  getApartmentGroups: async (buildingId?: string): Promise<ApartmentGroup[]> => {
    const response = await api.get('/apartments/groups', {
      params: buildingId ? { buildingId } : undefined,
    });
    return response.data;
  },

  getApartmentGroupById: async (id: string): Promise<ApartmentGroup> => {
    const response = await api.get(`/apartments/groups/${id}`);
    return response.data;
  },

  createApartmentGroup: async (data: CreateApartmentGroupData): Promise<ApartmentGroup> => {
    const response = await api.post('/apartments/groups', data);
    return response.data;
  },

  updateApartmentGroup: async (data: UpdateApartmentGroupData): Promise<ApartmentGroup> => {
    const { id, ...updateData } = data;
    const response = await api.patch(`/apartments/groups/${id}`, updateData);
    return response.data;
  },

  deleteApartmentGroup: async (id: string): Promise<void> => {
    await api.delete(`/apartments/groups/${id}`);
  },

  toggleApartmentGroupStatus: async (id: string): Promise<ApartmentGroup> => {
    const response = await api.patch(`/apartments/groups/${id}/toggle-status`);
    return response.data;
  },

  bulkAssignApartmentsToGroup: async (
    groupId: string,
    apartmentIds: string[],
  ): Promise<{ groupId: string; groupName: string; assignedCount: number }> => {
    const response = await api.patch(`/apartments/groups/${groupId}/assign-apartments`, {
      apartmentIds,
    });
    return response.data;
  },

  // Apartment Usage Types
  getApartmentUsageTypes: async (buildingId?: string): Promise<ApartmentUsageType[]> => {
    const response = await api.get('/apartments/usage-types', {
      params: buildingId ? { buildingId } : undefined,
    });
    return response.data;
  },

  getApartmentUsageTypeById: async (id: string): Promise<ApartmentUsageType> => {
    const response = await api.get(`/apartments/usage-types/${id}`);
    return response.data;
  },

  createApartmentUsageType: async (data: CreateApartmentUsageTypeData): Promise<ApartmentUsageType> => {
    const response = await api.post('/apartments/usage-types', data);
    return response.data;
  },

  updateApartmentUsageType: async (data: UpdateApartmentUsageTypeData): Promise<ApartmentUsageType> => {
    const { id, ...updateData } = data;
    const response = await api.patch(`/apartments/usage-types/${id}`, updateData);
    return response.data;
  },

  deleteApartmentUsageType: async (id: string): Promise<void> => {
    await api.delete(`/apartments/usage-types/${id}`);
  },

  toggleApartmentUsageTypeStatus: async (id: string): Promise<ApartmentUsageType> => {
    const response = await api.patch(`/apartments/usage-types/${id}/toggle-status`);
    return response.data;
  },

  exportApartmentUsageTypes: async (buildingId: string): Promise<void> => {
    const response = await api.get(`/apartments/usage-types/export?buildingId=${buildingId}`, {
      responseType: 'blob',
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || `apartment-usage-types-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  importApartmentUsageTypes: async (
    buildingId: string,
    file: File,
    mode: 'skip' | 'replace' = 'skip',
  ): Promise<UsageTypeImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('mode', mode);

    const response = await api.post('/apartments/usage-types/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Buildings (for dropdowns)
  getBuildings: async (): Promise<Building[]> => {
    const buildings: Building[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await api.get<BuildingsListResponse>(
        '/buildings',
        {
          params: { page, limit: BUILDINGS_SELECTOR_FETCH_LIMIT },
        },
      );
      const payload = response.data;
      if (isPaginatedBuildingsResponse(payload)) {
        buildings.push(...(payload.data as Building[]));
        totalPages = payload.meta.totalPages;
      } else {
        buildings.push(...(unwrapBuildingsList(payload) as Building[]));
        break;
      }
      page += 1;
    } while (page <= totalPages);

    return buildings;
  },

  // Apartment Members
  getApartmentMembers: async (apartmentId: string): Promise<ApartmentMember[]> => {
    const response = await api.get(`/apartment-members/apartment/${apartmentId}`);
    return response.data;
  },

  getResidentsPage: async (params?: ResidentListParams): Promise<PaginatedResidents> => {
    const response = await api.get('/apartment-members', { params });
    return response.data;
  },

  /** All residents in a building (for client-side search like Căn hộ list). */
  getResidentsByBuilding: async (buildingId: string): Promise<ApartmentMember[]> => {
    const members: ApartmentMember[] = [];
    let page = 1;
    let totalPages = 1;
    const limit = 500;
    do {
      const result = await apartmentsApi.getResidentsPage({ buildingId, page, limit });
      members.push(...result.data);
      totalPages = result.meta.totalPages;
      page += 1;
    } while (page <= totalPages);
    return members;
  },

  exportResidents: async (buildingId: string): Promise<void> => {
    const response = await api.get(`/apartment-members/export?buildingId=${buildingId}`, {
      responseType: 'blob',
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || `residents-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  downloadSampleResidents: async (): Promise<void> => {
    const response = await api.get('/apartment-members/sample-export', {
      responseType: 'blob',
    });

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_resident_import.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  importResidents: async (
    buildingId: string,
    file: File,
    mode: 'skip' | 'replace' = 'skip',
  ): Promise<{ success: boolean; message: string; errors?: string[]; importedCount?: number; updatedCount?: number; skippedCount?: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('mode', mode);

    const response = await api.post('/apartment-members/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  createApartmentMember: async (data: CreateApartmentMemberData): Promise<ApartmentMember> => {
    const response = await api.post('/apartment-members', data);
    return response.data;
  },

  updateApartmentMember: async (memberId: string, data: UpdateApartmentMemberData): Promise<ApartmentMember> => {
    const response = await api.patch(`/apartment-members/${memberId}`, data);
    return response.data;
  },

  deleteApartmentMember: async (memberId: string): Promise<void> => {
    await api.delete(`/apartment-members/${memberId}`);
  },

  bulkDeleteResidents: async (
    buildingId: string,
    memberIds: string[],
  ): Promise<ResidentBulkDeleteResult> => {
    const response = await api.post('/apartment-members/bulk-delete', {
      buildingId,
      memberIds,
    });
    return response.data;
  },

  listResidentActivityHistory: async (
    buildingId: string,
    options?: { page?: number; limit?: number },
  ): Promise<import('./paginatedHistory').PaginatedHistoryResponse<ResidentActivityHistoryItem>> => {
    const query = new URLSearchParams({ buildingId });
    if (options?.page) query.append('page', String(options.page));
    if (options?.limit) query.append('limit', String(options.limit));
    const response = await api.get(`/apartment-members/activity-history?${query.toString()}`);
    return response.data;
  },

  getApartmentMemberById: async (memberId: string): Promise<ApartmentMember> => {
    const response = await api.get(`/apartment-members/${memberId}`);
    return response.data;
  },

  getMembersByApartment: async (apartmentId: string): Promise<ApartmentMember[]> => {
    const response = await api.get(`/apartment-members/apartment/${apartmentId}`);
    return response.data;
  },

  getMembersByUser: async (userId: string): Promise<ApartmentMember[]> => {
    const response = await api.get(`/apartment-members/user/${userId}`);
    return response.data;
  },

  // Apartment Fee Categories
  getApartmentFeeCategories: async (apartmentId: string): Promise<ApartmentFeeCategory[]> => {
    const response = await api.get(`/apartments/${apartmentId}/fee-categories`);
    return response.data;
  },

  getAllApartmentFeeCategories: async (apartmentId: string): Promise<ApartmentFeeCategory[]> => {
    const response = await api.get(`/apartments/${apartmentId}/fee-categories/all`);
    return response.data;
  },

  updateApartmentFeeCategory: async (apartmentId: string, feeCategoryId: string, data: { quantity: number; quotaLimit?: number; notes?: string }): Promise<ApartmentFeeCategory> => {
    const response = await api.patch(`/apartments/${apartmentId}/fee-categories/${feeCategoryId}`, data);
    return response.data;
  },

  bulkUpdateApartmentFeeCategories: async (apartmentId: string, updates: { feeCategoryId: string; quantity: number; quotaLimit?: number; notes?: string }[]): Promise<ApartmentFeeCategory[]> => {
    const response = await api.post(`/apartments/${apartmentId}/fee-categories/bulk-update`, { updates });
    return response.data;
  },

  getApartmentActionLogs: async (
    apartmentId: string,
    options?: { page?: number; limit?: number },
  ) => {
    const query = new URLSearchParams();
    if (options?.page) query.append('page', String(options.page));
    if (options?.limit) query.append('limit', String(options.limit));
    const qs = query.toString();
    const response = await api.get(
      `/apartments/${apartmentId}/action-logs${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  },

  exportApartments: async (buildingId: string): Promise<void> => {
    const response = await api.get(`/apartments/export?buildingId=${buildingId}`, {
      responseType: 'blob',
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || `apartments-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  downloadApartmentImportSample: async (buildingId: string): Promise<void> => {
    const response = await api.get(`/apartments/import-sample?buildingId=${buildingId}`, {
      responseType: 'blob',
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || `sample-apartment-import-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  importApartments: async (buildingId: string, file: File): Promise<{
    success: boolean;
    message: string;
    batchId?: string;
    importedCount: number;
    updatedCount?: number;
    renamedCount?: number;
    quantityUpdateCount?: number;
    skippedCount?: number;
    skippedCodes?: string[];
    missingCount?: number;
    missingCodes?: string[];
    existingCount?: number;
    fileApartmentCount?: number;
    errorCount?: number;
    errorSummary?: ApartmentImportErrorSummary[];
    errors?: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    
    const response = await api.post('/apartments/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  listApartmentImportBatches: async (buildingId: string): Promise<ApartmentImportBatch[]> => {
    const response = await api.get(`/apartments/import-batches?buildingId=${buildingId}`);
    return response.data;
  },

  listApartmentActivityHistory: async (
    buildingId: string,
    options?: { page?: number; limit?: number },
  ): Promise<import('./paginatedHistory').PaginatedHistoryResponse<ApartmentActivityHistoryItem>> => {
    const query = new URLSearchParams({ buildingId });
    if (options?.page) query.append('page', String(options.page));
    if (options?.limit) query.append('limit', String(options.limit));
    const response = await api.get(`/apartments/activity-history?${query.toString()}`);
    return response.data;
  },

  revertApartmentImportBatch: async (
    batchId: string,
  ): Promise<{ restoredCount: number; revertedBy: string; fileName: string }> => {
    const response = await api.delete(`/apartments/import-batches/${batchId}`);
    return response.data;
  },

  sendApartmentFeeNotification: async (
    apartmentId: string,
    deliveryMethods: ('EMAIL' | 'APP')[],
  ): Promise<{
    success: boolean;
    emailsSent: number;
    appNotificationsSent: number;
    errors: string[];
  }> => {
    const response = await api.post(`/apartments/${apartmentId}/service-fees/send-notification`, {
      deliveryMethods,
    });
    return response.data;
  },

  exportApartmentFeePdf: async (
    apartmentId: string,
    options?: {
      billingPeriod?: string;
      onlyOutstanding?: boolean;
      feeCategoryIds?: string[];
    },
  ): Promise<{ message: string; id: string }> => {
    const response = await api.post(`/apartments/${apartmentId}/service-fees/export-pdf`, options || {});
    return response.data;
  },

  fetchApartmentFeeNoticePreviewHtml: async (
    apartmentId: string,
    options?: { detailed?: boolean },
  ): Promise<string> => {
    const response = await api.get(`/apartments/${apartmentId}/service-fees/fee-notice-preview.html`, {
      params: options?.detailed ? { detailed: 'true' } : undefined,
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return response.data as string;
  },
}; 